import { useEffect, useState, ChangeEvent } from 'react';
import InputField from './InputField';
import Card from './Cards';
import { useTranslation } from 'react-i18next';

type VolumeUnit = 'liter' | 'mililiter' | 'hectoliter' | 'cubicMeter' | 'cubicDecimeter' | 'cubicCentimeter' |
  'pintUs' | 'quartUs' | 'galUs' | 'pint' | 'quart' | 'gal' |
  'tspKan' | 'tspUs' | 'tsp' | 'tspBr' | 'tbspKan' | 'tbspUs' | 'tbsp' | 'tbspBr' |
  'cKan' | 'cUs' | 'c' | 'cBr';

type VolumeValues = Record<VolumeUnit, string | number>;

const coeffs: Record<VolumeUnit, number> = {
  liter: 1, mililiter: 1000, hectoliter: 1 / 100,
  cubicMeter: 1 / 1000, cubicDecimeter: 1, cubicCentimeter: 1000,
  pintUs: 2.113376419, quartUs: 1.056688209, galUs: 0.264172052,
  pint: 1.759753986, quart: 0.879876993, gal: 0.219969248,
  tspKan: 211.170478367, tspUs: 202.88413616, tsp: 200, tspBr: 168.936382694,
  tbspKan: 70.390159456, tbspUs: 67.628045398, tbsp: 66.666666667, tbspBr: 56.312127565,
  cKan: 4.399384966, cUs: 4.226752838, c: 4, cBr: 3.519507973,
};

function recalc(name: VolumeUnit, value: string): VolumeValues {
  const liters = Number(value) / coeffs[name];
  const next = {} as VolumeValues;
  for (const u in coeffs) {
    next[u as VolumeUnit] = (liters * coeffs[u as VolumeUnit]).toFixed(3);
  }
  next[name] = value;
  return next;
}

export default function Volume() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('volume.pageTitle');
  }, [t]);

  const [values, setValues] = useState<VolumeValues>(() => {
    const init = {} as VolumeValues;
    for (const u in coeffs) init[u as VolumeUnit] = (1 * coeffs[u as VolumeUnit]).toFixed(3);
    return init;
  });

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setValues(recalc(event.target.name as VolumeUnit, event.target.value));
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card title={t('volume.si')}>
          <InputField name="cubicMeter" label={t('volume.cubicMeter')} step="0.001" value={values.cubicMeter} unit="m³" onChange={handleOnChange} />
          <InputField name="cubicDecimeter" label={t('volume.cubicDecimeter')} step="0.1" value={values.cubicDecimeter} unit="dm³" onChange={handleOnChange} />
          <InputField name="cubicCentimeter" label={t('volume.cubicCentimeter')} step="0.1" value={values.cubicCentimeter} unit="cm³" onChange={handleOnChange} />
        </Card>
        <Card title={t('volume.metric')}>
          <InputField name="hectoliter" label={t('volume.hectoliter')} step="0.001" value={values.hectoliter} unit="hl" onChange={handleOnChange} />
          <InputField name="liter" label={t('volume.liter')} step="0.1" value={values.liter} unit="l" onChange={handleOnChange} />
          <InputField name="mililiter" label={t('volume.milliliter')} step="1" value={values.mililiter} unit="ml" onChange={handleOnChange} />
        </Card>
        <Card title={t('volume.usLiquid')}>
          <InputField name="pintUs" label={t('volume.pintUs')} step="0.1" value={values.pintUs} unit="pt" onChange={handleOnChange} />
          <InputField name="quartUs" label={t('volume.quartUs')} step="0.1" value={values.quartUs} unit="qt" onChange={handleOnChange} />
          <InputField name="galUs" label={t('volume.gallonUs')} step="0.1" value={values.galUs} unit="gal" onChange={handleOnChange} />
        </Card>
        <Card title={t('volume.imperial')}>
          <InputField name="pint" label={t('volume.pintBr')} step="0.1" value={values.pint} unit="pt" onChange={handleOnChange} />
          <InputField name="quart" label={t('volume.quartBr')} step="0.1" value={values.quart} unit="qt" onChange={handleOnChange} />
          <InputField name="gal" label={t('volume.gallonBr')} step="0.1" value={values.gal} unit="gal" onChange={handleOnChange} />
        </Card>
      </div>

      <Card title={t('volume.kitchen')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <InputField name="tspKan" label={t('volume.tspCanadian')} step="0.1" value={values.tspKan} unit="tsp" onChange={handleOnChange} />
            <InputField name="tspUs" label={t('volume.tspUs')} step="0.1" value={values.tspUs} unit="tsp" onChange={handleOnChange} />
            <InputField name="tsp" label={t('volume.tspMetric')} step="0.1" value={values.tsp} unit="tsp" onChange={handleOnChange} />
            <InputField name="tspBr" label={t('volume.tspImperial')} step="0.1" value={values.tspBr} unit="tsp" onChange={handleOnChange} />
          </div>
          <div>
            <InputField name="tbspKan" label={t('volume.tbspCanadian')} step="0.1" value={values.tbspKan} unit="tbsp" onChange={handleOnChange} />
            <InputField name="tbspUs" label={t('volume.tbspUs')} step="0.1" value={values.tbspUs} unit="tbsp" onChange={handleOnChange} />
            <InputField name="tbsp" label={t('volume.tbspMetric')} step="0.1" value={values.tbsp} unit="tbsp" onChange={handleOnChange} />
            <InputField name="tbspBr" label={t('volume.tbspImperial')} step="0.1" value={values.tbspBr} unit="tbsp" onChange={handleOnChange} />
          </div>
          <div>
            <InputField name="cKan" label={t('volume.cupCanadian')} step="0.1" value={values.cKan} unit="c" onChange={handleOnChange} />
            <InputField name="cUs" label={t('volume.cupUs')} step="0.1" value={values.cUs} unit="c" onChange={handleOnChange} />
            <InputField name="c" label={t('volume.cupMetric')} step="0.1" value={values.c} unit="c" onChange={handleOnChange} />
            <InputField name="cBr" label={t('volume.cupImperial')} step="0.1" value={values.cBr} unit="c" onChange={handleOnChange} />
          </div>
        </div>
      </Card>
    </div>
  );
}
